import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProduct } from '../api/products';
import { useCart } from '../contexts/CartContext';
import Rack, { RACK_COLORS, COLOR_LABEL, rowsToVariant } from '../components/Rack';

function formatPrice(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function inferModules(product) {
  if (product?.options?.modules) return product.options.modules;
  const slug = product?.slug || '';
  if (slug.includes('back')) return 3;
  if (slug.includes('trophy') || slug.includes('pair')) return 2;
  return 1;
}

const ROW_LABEL = (i, n) => {
  if (n === 1) return 'Row';
  if (n === 2) return i === 0 ? 'Top' : 'Bottom';
  if (n === 3) return ['Top', 'Middle', 'Bottom'][i];
  return `Row ${i + 1}`;
};

export default function ProductDetail() {
  const { slug } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([{ color: 'blue' }]);
  const [paint, setPaint] = useState('blue');
  const [selectedRow, setSelectedRow] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    setLoading(true);
    getProduct(slug)
      .then(res => {
        setProduct(res.data);
        const n = inferModules(res.data);
        setRows(Array.from({ length: n }, () => ({ color: 'blue' })));
        setSelectedRow(0);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const moduleCount = useMemo(() => inferModules(product), [product]);
  const isRack = product?.category === 'rack';

  const recolorRow = (i) => {
    setSelectedRow(i);
    setRows(prev => prev.map((r, idx) => idx === i ? { color: paint } : r));
  };
  const setPaintAndApply = (c) => {
    setPaint(c);
    setRows(prev => prev.map((r, idx) => idx === selectedRow ? { color: c } : r));
  };
  const applyToAll = () => {
    setRows(prev => prev.map(() => ({ color: paint })));
  };

  const handleAdd = async () => {
    if (!product) return;
    setAdding(true);
    try {
      const payload = { product_id: product.id, quantity };
      if (isRack) {
        payload.color = rows[0].color;
        payload.variant = moduleCount === 1
          ? (COLOR_LABEL[rows[0].color] || rows[0].color)
          : rowsToVariant(rows);
        payload.config = { rows: rows.map(r => ({ color: r.color })) };
      }
      await addItem(payload);
      setAdded(true);
      setTimeout(() => setAdded(false), 2000);
    } catch {}
    setAdding(false);
  };

  if (loading) return <div className="container section" style={{ textAlign: 'center' }}>Loading...</div>;
  if (!product) return (
    <div className="container section" style={{ textAlign: 'center' }}>
      <h2>Product not found</h2>
      <Link to="/shop" className="btn btn-secondary" style={{ marginTop: '16px' }}>Back to Shop</Link>
    </div>
  );

  return (
    <div className="container section">
      <Link to="/shop" className="mono" style={{ color: 'var(--gold-dim)', marginBottom: '24px', display: 'inline-block', transition: 'color 0.2s' }}>&larr; Back to Shop</Link>

      <div className="product-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '56px', marginTop: '16px' }}>
        {/* Preview */}
        <div>
          <div className="pdp-preview">
            {product.images?.length ? (
              <img src={product.images[selectedImage]} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : isRack ? (
              <div style={{ width: '78%' }}>
                <Rack rows={rows} onRowClick={recolorRow} selectedRow={selectedRow} />
                {moduleCount > 1 && (
                  <div className="pdp-row-tag">
                    <span className="mono">Painting:</span>
                    <span style={{ color: 'var(--cream)' }}>{ROW_LABEL(selectedRow, moduleCount)}</span>
                    <span className="mono" style={{ marginLeft: 'auto', color: 'var(--gold-dim)' }}>click any row to switch</span>
                  </div>
                )}
              </div>
            ) : (
              <span style={{ color: 'var(--gold-dim)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>No image</span>
            )}
          </div>
          {product.images?.length > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImage(i)}
                  style={{
                    width: '72px', height: '72px', overflow: 'hidden',
                    border: i === selectedImage ? '2px solid var(--gold)' : '1px solid rgba(201,168,76,0.1)',
                    background: 'var(--surface)', padding: 0, cursor: 'pointer', transition: 'border-color 0.2s'
                  }}
                >
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="mono" style={{ marginBottom: '12px' }}>{product.category?.toUpperCase()}</div>
          <h1 style={{ marginBottom: '16px', fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', fontWeight: 300 }}>{product.name}</h1>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '24px' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: 300, color: 'var(--gold)' }}>
              {formatPrice(product.price_cents)}
            </span>
            {product.compare_price_cents && (
              <span style={{ textDecoration: 'line-through', color: 'var(--cream-dim)', fontSize: '1rem', opacity: 0.5 }}>
                {formatPrice(product.compare_price_cents)}
              </span>
            )}
          </div>

          <div style={{ borderTop: '1px solid rgba(201,168,76,0.08)', borderBottom: '1px solid rgba(201,168,76,0.08)', padding: '20px 0', marginBottom: '28px' }}>
            <p style={{ color: 'var(--cream-dim)', lineHeight: '1.7', fontWeight: 300 }}>{product.description}</p>
          </div>

          {isRack && (
            <div style={{ marginBottom: '28px' }}>
              <div className="mono" style={{ display: 'block', marginBottom: '12px', fontSize: '0.6rem', color: 'var(--gold-dim)' }}>
                {moduleCount === 1 ? 'Color' : `Color — ${ROW_LABEL(selectedRow, moduleCount)} row`}
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                {RACK_COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setPaintAndApply(c)}
                    title={COLOR_LABEL[c] || c}
                    className={`chip chip-${c} ${paint === c ? 'active' : ''}`}
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '2px solid transparent', cursor: 'pointer', padding: 0 }}
                  />
                ))}
                {moduleCount > 1 && (
                  <button onClick={applyToAll} className="mono" style={{ marginLeft: 'auto', padding: '8px 14px', background: 'transparent', border: '1px solid rgba(201,168,76,0.2)', color: 'var(--cream-dim)', fontSize: '0.55rem', letterSpacing: '0.18em', cursor: 'pointer', transition: 'all 0.25s' }}>
                    Apply to all
                  </button>
                )}
              </div>
              <div className="pdp-summary">
                <span className="mono">Configuration:</span>
                <span style={{ color: 'var(--cream)' }}>{rowsToVariant(rows)}</span>
              </div>
            </div>
          )}

          <div style={{ marginBottom: '28px' }}>
            <div className="mono" style={{ display: 'block', marginBottom: '12px', fontSize: '0.6rem', color: 'var(--gold-dim)' }}>Quantity</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <button className="qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>-</button>
              <span style={{ fontFamily: 'var(--font-mono)', fontWeight: '500', minWidth: '28px', textAlign: 'center', fontSize: '1.1rem' }}>{quantity}</span>
              <button className="qty-btn" onClick={() => setQuantity(q => q + 1)}>+</button>
            </div>
          </div>

          <button className="btn btn-gold btn-lg" onClick={handleAdd} disabled={adding} style={{ width: '100%' }}>
            {added ? 'Added to Cart!' : adding ? 'Adding...' : 'Add to Cart'}
          </button>

          <p className="mono" style={{ textAlign: 'center', marginTop: '16px', color: 'var(--gold-dim)', fontSize: '0.55rem' }}>
            Free shipping on orders over $80
          </p>
        </div>
      </div>
    </div>
  );
}
